import React from 'react';

import {
	type DevDocsBylineConnectionAncestor,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { BylineConnectionsPlaceholderVisual } from '../BylineConnectionsPlaceholderVisual.tsx';
import { MAX_BYLINE_CONNECTION_ICONS } from '../utils/getBylineConnectionIcons.ts';

import { useOutboundBylineSsrState } from './useOutboundBylineSsrState.ts';

type BylineConnectionsPlaceholderProps = {
	contentId?: string;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
};

export const BylineConnectionsPlaceholder = ({
	contentId,
	connectionAncestors,
}: BylineConnectionsPlaceholderProps): React.JSX.Element | null => {
	// Read the preloaded outbound state on the client too (cache-only, hydrated from the SSR
	// preload). This lets the placeholder stay visible after the post-paint hydration and while the
	// LoadableAfterPaint chunk loads, instead of blanking — which caused the "SSR placeholder →
	// nothing → skeleton → item" flash. The result is derived purely from the hydrated cache and
	// dev-docs, so it is identical on the server and the client's first render (no hydration
	// mismatch). Pages with no connections still render nothing (no client footprint). CCINT-8752
	const outboundState = useOutboundBylineSsrState(contentId);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const shouldRenderPlaceholder =
		outboundState?.status === 'present' ||
		outboundState?.status === 'unavailable' ||
		devDocsConnection !== null;

	if (!shouldRenderPlaceholder) {
		return null;
	}

	// 'unavailable' gives no reliable estimate, so keep the maximum reservation; otherwise size to the
	// domain count plus the Dev Docs icon, floored to 1 and capped at MAX.
	let iconCount: number;
	if (outboundState?.status === 'unavailable') {
		iconCount = MAX_BYLINE_CONNECTION_ICONS;
	} else {
		const outboundIconCount = outboundState?.status === 'present' ? outboundState.iconCount : 0;
		const devDocsIconCount = devDocsConnection !== null ? 1 : 0;
		iconCount = Math.min(
			Math.max(outboundIconCount + devDocsIconCount, 1),
			MAX_BYLINE_CONNECTION_ICONS,
		);
	}

	return (
		<BylineConnectionsPlaceholderVisual
			testId="byline-connections-ssr-placeholder"
			iconCount={iconCount}
		/>
	);
};
